import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Drawer } from 'primeng/drawer';
import { environment } from '../../../../../environments/environment';
import { FEATURES_FLAGS } from '@shared/constants/features-flag.constant';
import { StorageService } from '@shared/services/auth/storage.service';
import { SharedChangesService } from '@shared/services/changes/shared-changes.service';
import { FeatureFlagService } from '@shared/services/feature-flags/feature-flag.service';

const SHOPBOX_AI_ORIGIN = 'https://shopbox-ai.shopbox.com';
const SHOPBOX_AI_LOCAL_ORIGIN = 'http://localhost:3000/';

@Component({
  selector: 'sb-shopbox-ai-panel',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [Drawer],
  templateUrl: './shopbox-ai-panel.component.html',
  styleUrls: ['./shopbox-ai-panel.component.scss'],
})
export class ShopboxAiPanelComponent {
  private readonly featureFlags = inject(FeatureFlagService);
  private readonly storage = inject(StorageService);
  private readonly sanitizer = inject(DomSanitizer);

  readonly enabled = computed(() => this.featureFlags.flags()[FEATURES_FLAGS.shopbox_ai] ?? false);
  readonly panelOpen = signal(false);
  readonly frameUrl = signal<SafeResourceUrl | null>(null);
  readonly drawerStyle = { width: 'min(640px, 100vw)', height: '100%' };

  constructor() {
    inject(SharedChangesService)
      .currentClientChange.pipe(takeUntilDestroyed())
      .subscribe(() => {
        if (this.panelOpen()) {
          this.refreshFrame();
        }
      });
  }

  open(): void {
    this.refreshFrame();
    this.panelOpen.set(true);
  }

  close(): void {
    this.panelOpen.set(false);
  }

  onVisibleChange(open: boolean): void {
    this.panelOpen.set(open);
  }

  private refreshFrame(): void {
    const clientId = this.storage.getClientUID();
    const accessToken = this.storage.getToken();
    if (!clientId || !accessToken) {
      this.frameUrl.set(null);
      return;
    }

    const url = new URL(this.aiOrigin());
    url.searchParams.set('clientId', String(clientId));
    url.searchParams.set('accessToken', String(accessToken));
    url.searchParams.set('env', environment.envName);
    this.frameUrl.set(this.sanitizer.bypassSecurityTrustResourceUrl(url.toString()));
  }

  private aiOrigin(): string {
    const host = window.location.hostname;
    if (host === 'localhost' || host === '127.0.0.1') {
      return SHOPBOX_AI_LOCAL_ORIGIN;
    }
    return SHOPBOX_AI_ORIGIN;
  }
}
